/**
 * A ZIP writer, small enough to keep rather than take a dependency for.
 *
 * Entries are STORED, never deflated. Everything this packs is already a
 * compressed format — PNG, JPEG, MP3, WebM — where deflate buys a percent or
 * two for the cost of pulling a compression library into the bundle.
 *
 * Only the parts of the format a reader needs are written: one local header per
 * entry, the central directory, and the end-of-central-directory record. No
 * Zip64, so an archive is good to 4 GB and any single entry to the same.
 */

const LOCAL_HEADER_SIG = 0x04034b50;
const CENTRAL_HEADER_SIG = 0x02014b50;
const END_OF_CENTRAL_DIR_SIG = 0x06054b50;
// "Stored, version 2.0", and bit 11 marks the file name as UTF-8.
const VERSION_NEEDED = 20;
const FLAG_UTF8_NAMES = 0x0800;
const METHOD_STORE = 0;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** MS-DOS date/time, the only timestamp the base format carries. */
function dosDateTime(date) {
  const year = Math.max(1980, date.getFullYear());
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

function encodeName(name) {
  return new TextEncoder().encode(name);
}

/**
 * Names must be unique inside an archive; a duplicate silently shadows the
 * earlier entry in most readers. Numbered suffixes keep both files.
 */
function uniqueName(name, taken) {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 2; ; i += 1) {
    const candidate = `${stem}-${i}${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Pack entries into a ZIP archive.
 *
 * @param {Array<{ path: string, bytes: Uint8Array }>} entries
 * @param {Date} [modifiedAt] - stamped on every entry
 * @returns {Uint8Array}
 */
export function buildZipBytes(entries, modifiedAt = new Date()) {
  const { time, date } = dosDateTime(modifiedAt);
  const taken = new Set();

  const prepared = (entries || [])
    .filter((entry) => entry && entry.path && entry.bytes)
    .map((entry) => {
      const path = uniqueName(String(entry.path), taken);
      taken.add(path);
      const bytes = entry.bytes instanceof Uint8Array ? entry.bytes : new Uint8Array(entry.bytes);
      return { nameBytes: encodeName(path), bytes, crc: crc32(bytes) };
    });

  const localSize = prepared.reduce(
    (sum, entry) => sum + 30 + entry.nameBytes.length + entry.bytes.length,
    0
  );
  const centralSize = prepared.reduce((sum, entry) => sum + 46 + entry.nameBytes.length, 0);

  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  let offset = 0;

  const writeU32 = (value) => {
    view.setUint32(offset, value, true);
    offset += 4;
  };
  const writeU16 = (value) => {
    view.setUint16(offset, value, true);
    offset += 2;
  };
  const writeBytes = (bytes) => {
    out.set(bytes, offset);
    offset += bytes.length;
  };

  for (const entry of prepared) {
    entry.offset = offset;
    writeU32(LOCAL_HEADER_SIG);
    writeU16(VERSION_NEEDED);
    writeU16(FLAG_UTF8_NAMES);
    writeU16(METHOD_STORE);
    writeU16(time);
    writeU16(date);
    writeU32(entry.crc);
    writeU32(entry.bytes.length); // compressed size — stored, so the same
    writeU32(entry.bytes.length);
    writeU16(entry.nameBytes.length);
    writeU16(0); // extra field length
    writeBytes(entry.nameBytes);
    writeBytes(entry.bytes);
  }

  const centralStart = offset;
  for (const entry of prepared) {
    writeU32(CENTRAL_HEADER_SIG);
    writeU16(VERSION_NEEDED); // version made by
    writeU16(VERSION_NEEDED);
    writeU16(FLAG_UTF8_NAMES);
    writeU16(METHOD_STORE);
    writeU16(time);
    writeU16(date);
    writeU32(entry.crc);
    writeU32(entry.bytes.length);
    writeU32(entry.bytes.length);
    writeU16(entry.nameBytes.length);
    writeU16(0); // extra field length
    writeU16(0); // comment length
    writeU16(0); // disk number
    writeU16(0); // internal attributes
    writeU32(0); // external attributes
    writeU32(entry.offset);
    writeBytes(entry.nameBytes);
  }

  // Taken before the record is written — the writers below move `offset`, and
  // measuring the directory afterwards would report it 12 bytes too long.
  const centralBytes = offset - centralStart;

  writeU32(END_OF_CENTRAL_DIR_SIG);
  writeU16(0); // this disk
  writeU16(0); // disk with the central directory
  writeU16(prepared.length);
  writeU16(prepared.length);
  writeU32(centralBytes);
  writeU32(centralStart);
  writeU16(0); // comment length

  return out;
}

/** Extensions for the MIME types a snapshot's assets actually arrive as. */
const EXTENSION_BY_MIME = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/wave": "wav",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/aac": "aac",
  "audio/x-aac": "aac",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "font/ttf": "ttf",
  "font/otf": "otf",
  "font/woff": "woff",
  "font/woff2": "woff2",
  "application/json": "json",
  "application/xml": "xml",
  "text/xml": "xml",
};

/** The MIME type a data URL declares, or "" for anything else. */
export function mimeFromDataUrl(dataUrl) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return "";
  const header = dataUrl.slice(5, dataUrl.indexOf(","));
  return header.split(";")[0].trim().toLowerCase();
}

/**
 * File extension for a data URL, without the dot. Unknown types fall back to
 * their MIME subtype ("audio/aiff" → "aiff"), which is right often enough and
 * never produces a name a filesystem would refuse.
 */
export function extensionFromDataUrl(dataUrl, fallback = "bin") {
  const mime = mimeFromDataUrl(dataUrl);
  if (!mime) return fallback;
  if (EXTENSION_BY_MIME[mime]) return EXTENSION_BY_MIME[mime];
  const subtype = mime.split("/")[1] || "";
  const cleaned = subtype.replace(/\+.*$/, "").replace(/[^a-z0-9]/g, "");
  return cleaned || fallback;
}

/** Decode a base64 data URL to its bytes; null for anything that is not one. */
export function dataUrlToBytes(dataUrl) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return null;
  const comma = dataUrl.indexOf(",");
  if (comma === -1) return null;
  const payload = dataUrl.slice(comma + 1);
  const isBase64 = /;base64/i.test(dataUrl.slice(0, comma));
  try {
    if (!isBase64) return new TextEncoder().encode(decodeURIComponent(payload));
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch (err) {
    return null;
  }
}
